import React, { ReactNode, useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'

import { ProviderTaskType } from '@lyrahealth-inc/shared-app-logic/src/models/providers/Providers'

import { actions as mixpanelActions, mixpanelEvents } from '../../../../../mixpanel/mixpanelConstants'
import { track } from '../../../../../mixpanel/mixpanelTracking'
import { getAuthUserId } from '../../../data/auth/authSelectors'
import {
  useCreateProviderManualTaskMutation,
  usePatchProviderTaskMutation,
} from '../../../providers/dashboard/data/providerTasksApi'
import { AddTaskModal } from '../components/taskModal/AddTaskModal'
import { EditTaskModal } from '../components/taskModal/EditTaskModal'
import { ManualTaskItem, TaskItem } from '../types'

type ManualTaskActions = {
  openEdit: (taskId: string) => void
  markDone: (taskId: string) => void
  openCreate: () => void
  modal: ReactNode
}

export const useManualTaskActions = (tasks: TaskItem[]): ManualTaskActions => {
  const userId = useSelector(getAuthUserId)
  const [patchTask] = usePatchProviderTaskMutation()
  const [createManualTask] = useCreateProviderManualTaskMutation()
  const [editingTaskId, setEditingTaskId] = useState<string | null>(null)
  const [isCreating, setIsCreating] = useState(false)

  const editingTask = useMemo(
    () => tasks.find((t): t is ManualTaskItem => t.kind === 'manual' && t.id === editingTaskId) ?? null,
    [tasks, editingTaskId],
  )

  const openEdit = useCallback((taskId: string) => setEditingTaskId(taskId), [])

  const openCreate = useCallback(() => setIsCreating(true), [])

  const markDone = useCallback(
    async (taskId: string) => {
      await patchTask({ id: taskId, kind: ProviderTaskType.MANUAL, data: { status: 'acknowledged' } }).unwrap()
    },
    [patchTask],
  )

  const handleConfirmEdit = useCallback(
    async (description: string) => {
      if (!editingTaskId) {
        return
      }
      await patchTask({
        id: editingTaskId,
        kind: ProviderTaskType.MANUAL,
        data: { arguments: { description } },
      }).unwrap()
    },
    [editingTaskId, patchTask],
  )

  const handleEditMarkDone = useCallback(async () => {
    if (!editingTaskId) {
      return
    }
    track({
      event: mixpanelEvents.BUTTON_PRESS,
      action: mixpanelActions.OPEN_ITEMS_HOVER_ACTION,
      properties: { panel: 'tasks', hover_action: 'mark_manual_done', origin: 'edit_modal' },
    })
    await patchTask({
      id: editingTaskId,
      kind: ProviderTaskType.MANUAL,
      data: { status: 'acknowledged' },
    }).unwrap()
  }, [editingTaskId, patchTask])

  const handleCreate = useCallback(
    async (description: string) => {
      await createManualTask({ providerId: userId, data: { description } }).unwrap()
      track({ event: mixpanelEvents.OPEN_ITEMS_MANUAL_TASK_CREATED })
    },
    [createManualTask, userId],
  )

  const modal = (
    <>
      <EditTaskModal
        visible={editingTask !== null}
        initialDescription={editingTask?.description ?? ''}
        onConfirm={handleConfirmEdit}
        onMarkDone={handleEditMarkDone}
        onClose={() => setEditingTaskId(null)}
      />
      <AddTaskModal visible={isCreating} onAdd={handleCreate} onClose={() => setIsCreating(false)} />
    </>
  )

  return { openEdit, markDone, openCreate, modal }
}
